import * as React from "react";
import { useIntl } from "react-intl";
import { Routine } from "redux-saga-routines";
import {Action, ActionFunctionAny} from "redux-actions";
import styled from "styled-components";
import { getMessages, sendMessage } from "../Store/routines";
import { connect } from "react-redux";
import { IMessage } from "../types";
import moment from "moment";
import { AppState } from "@Safemate/Store/types";
import { PrimaryButton, TextInput } from "./styles";

const MAX_LENGTH = 20;

const Wrapper = styled.div`
  width: 100%;
`;

const HistoryWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column-reverse;
  gap: 8px;
  max-height: 240px;
  overflow-y: auto;
  margin-bottom: 12px;
`;

const Bubble = styled.div`
  border: 1px solid ${props => props.theme.colors.borderColor};
  border-radius: 10px;
  background: rgba(128, 128, 128, 0.08);
`;

const MessageText = styled.div`
  padding: 10px 12px 6px;
  font-size: ${props => props.theme.font.normal};
  color: ${props => props.theme.colors.textPrimary};
  word-break: break-word;
`;

const MessageMeta = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 0 12px 8px;
  font-size: ${props => props.theme.font.small};
  color: ${props => props.theme.colors.textSecondary};
`;

const DateText = styled.span`
  min-width: 0;
`;

const StatusText = styled.span`
  font-style: italic;
  text-transform: lowercase;
  white-space: nowrap;
`;

const EmptyState = styled.div`
  padding: 20px 12px;
  text-align: center;
  font-size: ${props => props.theme.font.small};
  color: ${props => props.theme.colors.textSecondary};
`;

const Composer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CharCount = styled.span`
  font-size: ${props => props.theme.font.small};
  color: ${props => props.theme.colors.textSecondary};
  opacity: 0.7;
  white-space: nowrap;
`;

export enum MessageStatusEnum{
  PENDING = "PENDING",
  NOT_DELIVERED = "NOT_DELIVERED",
  RECEIVED = "RECEIVED",
  READ = "READ",
  NOT_READ = "NOT_READ"
}

const mapStateToProps = ({ settings: {initialization: { deviId }, info: { messages }}}: AppState) => {
  return {
    deviId,
    messages
  }
}

const mapDispatchToProps = {
  sendMessage,
  getMessages
}

interface MessageProps{
  sendMessage: Routine<ActionFunctionAny<Action<any>>>;
  getMessages: Routine<ActionFunctionAny<Action<any>>>;
  deviId: number;
  messages: IMessage[];
}

const Message = connect(mapStateToProps, mapDispatchToProps)(({sendMessage, getMessages, messages, deviId}: MessageProps) => {

  const { formatMessage } = useIntl();
  const [ message, setMessage ] = React.useState("");

  React.useEffect(() => {
    getMessages(deviId);

    const refresh = setInterval(() => {
      getMessages(deviId);
    }, 10000);
    return () => {
      clearTimeout(refresh);
    }

  }, [])

  const sendMsg = () => {
    if(!message.trim()) return;
    sendMessage({
      deviId,
      message
    })
    setMessage("");
  }

  return(
    <Wrapper>
      <HistoryWrapper>
        {messages.length > 0
          ? messages.sort(sortMessages).map((message, index) => {
              return (
                <Bubble key={index}>
                  <MessageText>
                    {message.message}
                  </MessageText>
                  <MessageMeta>
                    <DateText>
                      {moment(message.created).format("LLLL")}
                    </DateText>
                    <StatusText>
                      {formatMessage({id: `MESSAGE_STATUS_${message.status}`, defaultMessage: message.status})}
                    </StatusText>
                  </MessageMeta>
                </Bubble>
              )
            })
          : <EmptyState>
              {formatMessage({id: "noMessageHistory", defaultMessage: "Ingen meldingshistorikk"})}
            </EmptyState>
        }
      </HistoryWrapper>
      <Composer>
        <TextInput
          maxLength={MAX_LENGTH}
          value={message}
          placeholder={formatMessage({id: "writeMessage", defaultMessage: "Skriv en melding"})}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => { if(e.key === "Enter") sendMsg(); }}
          type="text"
        />
        <CharCount>{`${message.length}/${MAX_LENGTH}`}</CharCount>
        <PrimaryButton onClick={sendMsg} disabled={!message.trim()}>
          {formatMessage({id: "send", defaultMessage: "Send"})}
        </PrimaryButton>
      </Composer>
    </Wrapper>
  )
})

const sortMessages = (a: IMessage, b: IMessage) => {
  return a.created > b.created ? -1 : 1;
}

export default Message;
